import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ShieldCheck } from "lucide-react";
import api, { formatError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const DEPOSIT_PCT = 0.30;
const COMMISSION_PCT = 0.05;

export default function BuyRequestModal({ wine, onClose }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        importer_company: "",
        importer_country: "",
        quantity_cases: 100,
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);

    if (!wine) return null;

    const bottles = Math.max(1, Number(form.quantity_cases || 0)) * 12;
    const total = bottles * (wine.price_fob_usd || 0);
    const deposit = total * DEPOSIT_PCT;
    const commission = total * COMMISSION_PCT;

    const submit = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.info("Iniciá sesión para solicitar una compra.");
            navigate("/login?redirect=/wines");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/deals", {
                wine_id: wine.id,
                quantity_cases: Number(form.quantity_cases),
                importer_company: form.importer_company,
                importer_country: form.importer_country,
                notes: form.notes,
            });
            toast.success("Contrato preliminar generado.");
            onClose();
            navigate(`/deal/${data.id}`);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="buy-modal">
            <div className="bg-[#0A0A0A] border border-[#27272A] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between p-8 border-b border-[#27272A]">
                    <div>
                        <p className="overline mb-2">Solicitar Compra</p>
                        <h3 className="font-display text-3xl">{wine.name}</h3>
                        <p className="text-sm text-[#A1A1AA] mt-1">{wine.winery_name} · {wine.vintage}</p>
                    </div>
                    <button onClick={onClose} className="text-[#71717A] hover:text-white" data-testid="buy-close"><X /></button>
                </div>

                <form onSubmit={submit} className="p-8 space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <label className="overline block mb-2">Empresa</label>
                            <input required className="input-line" data-testid="buy-company" value={form.importer_company} onChange={(e) => setForm({ ...form, importer_company: e.target.value })} />
                        </div>
                        <div>
                            <label className="overline block mb-2">País</label>
                            <input required className="input-line" data-testid="buy-country" value={form.importer_country} onChange={(e) => setForm({ ...form, importer_country: e.target.value })} />
                        </div>
                        <div className="md:col-span-2">
                            <label className="overline block mb-2">Cantidad (cajas de 12)</label>
                            <input required type="number" min="1" className="input-line" data-testid="buy-quantity" value={form.quantity_cases} onChange={(e) => setForm({ ...form, quantity_cases: e.target.value })} />
                        </div>
                    </div>

                    <textarea
                        className="input-line resize-none"
                        rows="3"
                        placeholder="Notas opcionales: timing, mercado destino, condiciones..."
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        data-testid="buy-notes"
                    />

                    <div className="border border-[#27272A] p-6 space-y-2 text-sm" data-testid="buy-summary">
                        <Row label="Botellas" value={bottles.toLocaleString()} />
                        <Row label="Precio FOB / botella" value={`USD ${(wine.price_fob_usd || 0).toFixed(2)}`} />
                        <Row label="Total FOB" value={`USD ${total.toFixed(2)}`} bold />
                        <div className="border-t border-[#27272A] my-3" />
                        <Row label="Seña 30% (directo a bodega)" value={`USD ${deposit.toFixed(2)}`} gold />
                        <Row label="Comisión CellarGlobal 5%" value={`USD ${commission.toFixed(2)}`} />
                    </div>

                    <div className="flex items-start gap-3 text-xs text-[#A1A1AA] border-l-2 border-[#C9A961] pl-4">
                        <ShieldCheck className="w-4 h-4 text-[#C9A961] shrink-0 mt-0.5" />
                        <p>
                            Al confirmar, generamos un contrato PDF con 3 firmas (bodega · exportador · CellarGlobal 5%).
                            El chat queda activo y los datos de contacto personal permanecen bloqueados hasta que la bodega
                            confirme la seña recibida.
                        </p>
                    </div>

                    <button type="submit" disabled={submitting} className="btn-gold w-full" data-testid="buy-submit">
                        {submitting ? "Generando contrato..." : "Generar Contrato y Continuar"}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Row({ label, value, bold, gold }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-[#A1A1AA]">{label}</span>
            <span className={`${bold ? "font-semibold" : ""} ${gold ? "text-[#C9A961]" : "text-white"}`}>{value}</span>
        </div>
    );
}
